/**
 * RxOverView Component
 *
 * Container component for managing and displaying prescription (Rx) data for a patient.
 * This is the main layout component that orchestrates the display of individual prescriptions,
 * prescription series, and related functionality within the patient overview section.
 *
 * Key Features:
 * - Displays list of prescriptions with selection capability
 * - Shows detailed prescription information and editing capabilities
 * - Manages prescription series and therapy series
 * - Provides quick-add functionality for new prescriptions
 * - Handles appointment viewing and printing
 * - Manages line item display for billing
 * - Supports prescription deletion, restoration, and virtual prescription creation
 * - Integrates with ticket creation and doctor report functionality
 *
 * Layout Structure:
 * - Left panel: Prescription selector, series selector, and quick-add controls
 * - Right panel: Detailed prescription view with appointments and line items
 * - Various dialogs: Edit forms, ticket creation, appointment printing, etc.
 *
 * State Management:
 * - Uses MobX for reactive state management
 * - Integrates with RxStore, PatientsStore, and AppointmentStore
 * - Handles complex state interactions between prescriptions and appointments
 *
 * @param setPatientData - Function to update patient data
 * @param handleData - Function to handle data changes
 * @param masterData - Master data for dropdowns and validation
 * @param routingParams - URL routing parameters
 * @param noNavigate - Flag to disable navigation
 * @param handleDoctorReportView - Function to handle doctor report viewing
 */

import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Grid } from "@mui/material";

import { AppointmentDto, PatientDto, RxDto, RxSeriesDto, TherapyRxDto } from "../../../api-client";
import { RxStoreContext } from "../../../stores/Rx/rx.provider";
import useStore from "../../../helpers/useStore";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { observer } from "mobx-react";
import { visitTypeMapping } from "../../../helpers/visitTypeMapping";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { usePatientRoute } from "../../../routes/usePatientRoute";
import { validateFrequency, validateTherapy } from "../../../molecules/Editable";
import notificationStore from "../../../stores/Notification/notificationStore";
import RxRowOverview from "./RxRowOverview";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import TerminView from "./TerminView";
import RxTerminView from "./RxTerminView";
import {
  useMasterTables,
  getTherapyId as utilGetTherapyId,
  getTarifGroupForRxType as utilGetTarifGroupForRxType,
} from "../../../stores/MasterTables";
import RxSeriesSelector from "./RxSeries/RxSeriesSelector";
import RxSeriesRowView from "./RxSeries/RxSeriesRowView";
import TherapyRxSeries from "./RxSeries/TherapyRxSeries";
import { TerminViewConstants } from "../TerminView";
import RxFormsData from "./RxForms/RxFormsData";
import { UserContext } from "../../../stores/User/User.provider";
import LineItemViewer from "../../../molecules/LineItemViewer";
import { usePatient } from "../../../stores/Patients";
import { RxSelectorCard } from "./RxSelectorCard";
import NewRxCard from "./NewRxCard";
import { useRxSeries } from "../../../stores/Rx/rx.queries";
import SeriesTerminView from "./RxSeries/SeriesTerminView";

export interface RxType {
  id: string;
  text: string;
}

interface RxOverViewProps {
  openPatientTab: (row: any) => void;
  setPatientData: (row: any) => void;
  handleData: (row: any) => void;
  masterData?: any;
  routingParams?: any;
  noNavigate?: boolean;
  handleDoctorReportView?: (rxId: string) => void;
}

export type QuickRxData = {
  rxTypeData: RxType;
  selectedTarifGroup: string;
  rxAccount: string;
  tableData: any[];
};

const RxOverView: React.FC<RxOverViewProps> = observer(
  ({ setPatientData, handleData, routingParams, noNavigate, handleDoctorReportView }) => {
    const [editRxMode, setEditRxMode] = useState<boolean>(false);
    const [openSeriesView, setSeriesOpenView] = useState<boolean>(false);


    // const [selectedTarifGroup, setSelectedTarifGroup] = useState("");
    // TODO load ids or data

    const { selectedPatientId, selectedSeriesId, selectedRxId } = useStore(PatientsContext);

    // useEffect(() => {
    //   console.debug("Selected Rx ID changed:", selectedRxId);
    //   setSeriesOpenView(false);
    // }, [selectedRxId]);

    return (
      <Box
        sx={{
          border: "0px dotted black",
          padding: "8px",
          boxSizing: "border-box",
          width: "auto",
        }}
      >
        <Grid container sx={{ border: "0px dotted red" }} justifyContent="flex-start" spacing={2}>
          {/* Left side components */}
          <Grid item xs={12} md={3} sx={{ border: "0px solid red" }}>
            <Grid container>
              <Grid item xs={12}>
                {/* Keyed on the patient so the quick-add draft cannot outlive a
                    patient switch and be posted for the next one. */}
                <NewRxCard key={selectedPatientId ?? "none"} />
              </Grid>
              <Grid item xs={12}>
                <RxSelectorCard />
              </Grid>
              <Grid item sx={{ width: "100%", paddingTop: "20px" }}>
                <Card sx={{ padding: "0px" }}>
                  <CardHeader title="Behandlungsserien" />

                  <CardContent sx={{ padding: "0px", overflowX: "auto" }}>
                    <RxSeriesSelector />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>{" "}
            {/* End of grid container*/}
          </Grid>{" "}
          {/* End of grid item for left column*/}
          {/* Right side */}
          {selectedRxId && (
            <Grid item xs={12} md={9} sx={{ border: "0px dashed blue" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                  {" "}
                  {/* Set RxRowOverview to take 12 columns on medium screens and above */}
                  <RxRowOverview
                    editRxMode={editRxMode}
                    setEditRxMode={setEditRxMode}
                    handleDoctorReportView={handleDoctorReportView}
                    selectedRxId={selectedRxId}
                    patientId={selectedPatientId}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {/* Set the extra Box to take 12 columns on medium screens and above */}

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader title="Termine" />
                        <CardContent>
                          <RxTerminView rxId={selectedRxId} patientId={selectedPatientId} />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader title="Positionen" />
                        <CardContent>
                          {/* Checking billing needs the positions every time,
                              so they load with the prescription rather than
                              behind a "laden" click. */}
                          <LineItemViewer rxId={selectedRxId} autoload />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {selectedSeriesId && (
            <Grid item xs={12} md={9} sx={{ border: "0px dashed green" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                  <RxSeriesRowView
                    selectedSeriesId={selectedSeriesId!}
                    selectedPatientId={selectedPatientId!}
                    setSeriesOpenView={setSeriesOpenView}
                  />

                  <TherapyRxSeries
                    selectedSeriesId={selectedSeriesId!}
                    selectedPatientId={selectedPatientId!}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {" "}
                  <Card>
                    <CardHeader title="Termine" />
                    <CardContent>
                      {/* Set the extra Box to take 12 columns on medium screens and above */}
                      <SeriesTerminView seriesId={selectedSeriesId!} patientId={selectedPatientId} />
                    </CardContent>
                  </Card>
                </Grid>          
                
              </Grid>
            </Grid>
          )}     
          {/* <Dialog
            open={editRxMode}
            onClose={handleClose}
            aria-labelledby="edit-rx-dialog"
            fullScreen
          >
            <DialogContent> */}
          {editRxMode && (
            <RxFormsData
              isEditing={true}
              openRx={editRxMode}
              onCloseRx={()=>{setEditRxMode(false)}}
              useDialog={true}
              selectedRxId={selectedRxId || undefined}
              selectedPatientId={selectedPatientId!}
              //  setUnsavedChanges={setUnsavedChanges}
            />
            //    <RxPage
            //    isEditing={true}
            //    selectedRow={selectedRow}
            //    patientValues={patientData}
            //    masterData={masterData}
            //    handleData={handleData}
            //    setPatientData={(row: any) => setPatientData(row)}
            //    openRx={editRxMode}
            //    onCloseRx={handleClose}
            //    useDialog={true}
            //  />
          )}
          {/* </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Schließen</Button>
            </DialogActions>
          </Dialog> */}
        </Grid>
      </Box>
    );
  }
);

export default RxOverView;
